import { SafeError, SafePromise } from '@benzinga/safe-await';
import { Session } from '@benzinga/session';
import { ExtendedListenableSubscribable } from '@benzinga/subscribable';
import { egressMarketingWinRequestParams } from './egress';
import { MarketingWin } from './entities';
import { ingressMarketingWinResponse } from './ingress';
import { MarketingWinsRestful } from './restful';
import { MarketingWinsStore } from './store';

interface MarketingWinsFunctions {
  getWins: MarketingWinsRequest['getWins'];
}

interface MarketingWinsErrorEvent {
  error?: SafeError;
  errorType: 'get_wins_error';
  type: 'error';
}

interface MarketingWinsEvents {
  wins: MarketingWin[];
  type: 'wins_received';
}

export type MarketingWinsRequestEvent = MarketingWinsEvents | MarketingWinsErrorEvent;

export interface MarketingWinsRequestParams {
  limit?: number;

  skip?: number;
}

export class MarketingWinsRequest extends ExtendedListenableSubscribable<
  MarketingWinsRequestEvent,
  MarketingWinsFunctions
> {
  private restful: MarketingWinsRestful;
  private prevParams?: MarketingWinsRequestParams;
  private shouldFetch: boolean;
  private store: MarketingWinsStore;

  constructor(session: Session) {
    super();
    this.shouldFetch = true;
    this.restful = new MarketingWinsRestful(session);
    this.store = new MarketingWinsStore();
  }

  public getWins = async (force?: boolean, params?: MarketingWinsRequestParams): SafePromise<MarketingWin[]> => {
    if (force || (params?.limit ?? 0) > (this.prevParams?.limit ?? 0) || this.shouldFetch) {
      const safeWins = await this.restful.getWins(egressMarketingWinRequestParams(params));

      if (safeWins.err) {
        this.dispatch({
          error: safeWins.err,
          errorType: 'get_wins_error',
          type: 'error',
        });
        return {
          err: safeWins.err,
        };
      }

      const wins = ingressMarketingWinResponse(safeWins.ok);

      this.dispatch({
        type: 'wins_received',
        wins: wins,
      });

      this.store.updateWins(wins);

      this.shouldFetch = false;
      setTimeout(
        () => {
          this.shouldFetch = true;
        },
        1000 * 60 * 15,
      );

      return {
        ok: wins,
      };
    } else {
      return { ok: this.store.getWins() };
    }
  };

  protected onSubscribe(): MarketingWinsFunctions {
    return {
      getWins: this.getWins,
    };
  }
}
